/**
 * Le brain, côté état et actions.
 *
 * CE QUE CE STORE REMPLACE
 *
 * `brain.js` était une CLASSE instanciée impérativement, étrangère à Vue. Les
 * autres stores ne pouvaient l'atteindre que par une référence globale : d'où
 * `window.b`, puis `brainInstance` pour l'assainir. Le portage a d'abord
 * conservé ce contrat à dessein — c'est lui qui a permis de remplacer 1 915
 * lignes sans toucher un seul store ni une seule machine à états.
 *
 * Mais le sens de la dépendance y était inversé : des STORES appelaient des
 * méthodes d'un COMPOSANT, et `answers.store` allait jusqu'à écrire dans un
 * tableau qui ne lui appartenait pas (`brainInstance?.data.push(item)`). Le
 * `?.` masquait silencieusement le cas « pas encore monté », où l'item partait
 * dans le vide.
 *
 * Ici l'état vit dans un store, comme le reste de l'app. `BrainBoard` n'est
 * plus qu'une vue : il démarre, il rend, il arrête.
 *
 * SUR LES APPELS AUX AUTRES STORES
 *
 * Ils se font DANS les actions, jamais au niveau du `setup`. Plusieurs de ces
 * stores nous appellent en retour (`answers`, `testTimer`, `brainTuto`) : les
 * résoudre à la construction créerait un cycle d'import. Les résoudre à
 * l'appel, non — Pinia est déjà initialisé à ce moment-là.
 */
import { useWindowScroll } from "@vueuse/core"
import { defineStore } from "pinia"
import { computed, watch } from "vue"

import { useImportantModalStore } from "@/features/brain/components/ImportantModal/importantModal.store"
import { MODAL_TUTO_STEP } from "@/features/brain/components/ImportantModal/items"
import { useTestTimerStore } from "@/features/brain/components/TestTimer/testTimer.store"
import {
  useBrainRunner,
  type BrainItem,
} from "@/features/brain/composables/useBrainRunner"
import { TUTO_DATA } from "@/features/brain/data/tutoData"
import { brainActor } from "@/features/brain/machines/brainMachine"
import { useAnswersStore } from "@/features/brain/stores/answers.store"
import { useBrainLayoutStore } from "@/features/brain/stores/brainLayout.store"
import { useBrainTutoStore } from "@/features/brain/stores/brainTuto.store"

export const useBrainStore = defineStore("brain", () => {
  const runner = useBrainRunner()

  /**
   * À quoi correspond le contenu de `runner.data`.
   *
   * `start()` est rappelé à chaque remontage de `BrainBoard` — changement de
   * langue, écran de respiration — et doit alors réutiliser la file. Mais
   * après une sortie de tutoriel ou un redémarrage, la file en mémoire ne
   * décrit plus le mode courant et doit être jetée. Ce témoin distingue les
   * deux cas sans que chaque appelant ait à y penser.
   */
  let dataMode: "tuto" | "test" | null = null

  /** Vrai entre `start()` et `stop()`. Garde les actions d'un appel hors vie. */
  const isRunning = computed(() => runner.currentTest.value > -1)

  /** Vrai pendant la démonstration du tutoriel. */
  const isPlayingAnswer = computed(
    () => runner.animation.value?.isPlayingAnswer.value ?? false
  )

  /** Les étapes de la bonne réponse d'une étape du tutoriel. */
  function tutorialAnswer(step: number) {
    const data = TUTO_DATA[step] as unknown as {
      data?: { answer?: { token: string; dropzone: string }[] }
    }
    return data?.data?.answer ?? []
  }

  /**
   * Ajoute un item à la file.
   *
   * Remplace le `brainInstance?.data.push(item)` d'`answers.store` : le store
   * qui récupère l'item le transmet, il n'écrit plus dans le tableau d'un
   * autre. Renvoie `false` si le brain n'est pas démarré, au lieu de laisser
   * l'item disparaître en silence.
   */
  function addItem(item: BrainItem): boolean {
    if (!item?.data) return false
    runner.data.push(item)
    return true
  }

  /** Construit et affiche l'item d'indice donné. */
  async function showItem(index: number) {
    const layout = useBrainLayoutStore()
    const tuto = useBrainTutoStore()
    const timer = useTestTimerStore()

    layout.trackMoves = 0
    layout.trackResets = 0
    layout.trackOversights = 0

    // « Voir la réponse » attend que la scène ait fini d'apparaître.
    if (tuto.isTutorial) tuto.tutoIsReady = false
    if (!runner.build(index)) return
    await runner.show(() => {
      if (tuto.isTutorial) {
        runner.blinkEmptyDropzones()
        tuto.tutoIsReady = true
      } else {
        timer.startTimer(runner.item.value?.timer ?? 180)
      }
      layout.allowActions = true
    })
  }

  /** brain.js:219-256 — remise à zéro immédiate, sans animation. */
  function rawResetTest() {
    const layout = useBrainLayoutStore()
    layout.trackResets++
    runner.reset()
    if (useBrainTutoStore().isTutorial) layout.hideCTA()
    else layout.showSkipCTA()
  }

  /** brain.js:92-120 — envoie la réponse, puis enchaîne sur l'item suivant. */
  async function sendAnswer() {
    const layout = useBrainLayoutStore()
    const timer = useTestTimerStore()
    const answers = useAnswersStore()

    if (!layout.allowActions) return
    layout.allowActions = false

    timer.stopTimer()
    // L'ORDRE COMPTE. `getElapsedTime` se calcule en `totalDuration - time` :
    // lu après la remise à zéro, il vaudrait la durée entière de l'item.
    const timeInitial = timer.getElapsedTime
    // Puis on efface l'affichage : entre deux items le chronomètre montre
    // « --:-- » au lieu de rester figé puis de sauter à la durée du suivant.
    timer.resetTimer()

    try {
      const response = await answers.patchTest(
        runner.computeResponse({
          timeInitial,
          moves: layout.trackMoves,
          resets: layout.trackResets,
          oversights: layout.trackOversights,
        })
      )

      await runner.hide()
      runner.data.length = 0
      runner.data.push(response)

      // brain.js:112-118 — au dernier item, le backend renvoie
      // `test_completed` et plus aucune scène exploitable. Sans cette branche,
      // on enchaîne sur `showItem`, `build()` échoue en silence et la machine
      // ne reçoit jamais `done` : le candidat reste sur une page blanche.
      if (response?.test_completed) {
        runner.data.length = 0
        dataMode = null
        brainActor.send({ type: "done" })
        return
      }

      // `showBreath` ne fait plus que compter la réponse : l'écran de
      // respiration des réponses 1 et 5 a été supprimé par le redesign.
      await answers.showBreath()
      await showItem(0)
    } finally {
      layout.allowActions = true
    }
  }

  /** brain.js:486-587 — le tutoriel joue la bonne réponse. */
  async function playAnswer(tutorialID: number) {
    await runner.playAnswer(tutorialAnswer(tutorialID))
    useBrainTutoStore().tutoAnswerIsPlaying = false
  }

  /**
   * Garde de réentrance — brain.js:214.
   *
   * Deux appels concurrents construiraient deux fois la même étape et, sur la
   * dernière, enverraient `done` deux fois — ce qui saute la modale de fin de
   * tutoriel (vérifié dans `brainMachine.ts` : `tuto` -> `tutoConfirmation`
   * -> `test`).
   */
  let isMovingToNextStep = false

  /**
   * brain.js:209-243 — étape suivante du tutoriel, ou rejoue la même.
   *
   * Le point à ne pas rater : l'étape n'est incrémentée QUE s'il en existe une
   * suivante. L'incrémenter d'abord fait pointer l'instruction de `TutoView`
   * sur un item inexistant et plante l'écran de fin.
   */
  async function nextTutorialStep(retry: boolean) {
    if (isMovingToNextStep) return
    isMovingToNextStep = true

    const layout = useBrainLayoutStore()
    const tuto = useBrainTutoStore()

    try {
      const next = retry ? tuto.currentTutoStep : tuto.currentTutoStep + 1

      await runner.hide()

      const { y } = useWindowScroll({ behavior: "smooth" })
      if (y.value > 0) y.value = 0
      layout.hideCTA()

      if (next > TUTO_DATA.length - 1) {
        tuto.isTutorial = false
        // Sortie du tutoriel : c'est ICI que la file se purge, pas au
        // démontage. Sans ça, `start()` retrouverait les étapes de tutoriel en
        // mémoire et rejouerait la première au lieu d'aller chercher le
        // premier item réel.
        runner.data.length = 0
        dataMode = null
        brainActor.send({ type: "done" })
        return
      }

      tuto.currentTutoStep = next
      await showItem(next)
    } finally {
      isMovingToNextStep = false
    }
  }

  /**
   * brain.js:589-606 — validation d'une étape du tutoriel.
   *
   * L'original teste `allDropzonesAreFilled()` puis fait la MÊME chose dans
   * les deux branches (591-604). La condition est sans effet, on ne la
   * reproduit pas.
   */
  function onSubmitTutoStep() {
    const layout = useBrainLayoutStore()
    const tuto = useBrainTutoStore()
    const { openModal } = useImportantModalStore()

    if (runner.isTutorialAnswerCorrect(tutorialAnswer(tuto.currentTutoStep))) {
      layout.hideCTA()
      openModal(MODAL_TUTO_STEP[tuto.currentTutoStep as 0 | 1 | 2 | 3], {
        actionPrimary: () => void nextTutorialStep(false),
        actionSecondary: () => void nextTutorialStep(true),
      })
    } else {
      // `isResetting` grise les boutons de `TutoView` le temps du message
      // d'erreur, et l'efface quand il retombe.
      layout.isResetting = true
      void tuto.openTutoErrorModal().then(() => {
        if (!tuto.tutoAnswerIsPlaying) rawResetTest()
        layout.isResetting = false
      })
    }
  }

  /** brain.js:287-306 — appelé toutes les 20 s par le minuteur. */
  function setDropzonesBlink() {
    runner.blinkEmptyDropzones()
  }

  /** brain.js:308-336 — le temps est écoulé. */
  function userIsIdle() {
    const layout = useBrainLayoutStore()
    const { openModal } = useImportantModalStore()

    if (layout.trackMoves === 0 && !runner.anyDropzoneFilled.value) {
      openModal("idleTwo", { actionPrimary: () => window.location.reload() })
      window.setTimeout(() => window.location.reload(), 10000)
    } else {
      openModal("idleOne", { actionPrimary: () => void sendAnswer() })
    }
  }

  /**
   * Démarre le brain. Appelé par `BrainBoard` à son montage.
   *
   * brain.js:68-78 — deux amorçages distincts selon le mode.
   *
   * IDEMPOTENT, ET C'EST ESSENTIEL. `brain.js` vivait dans un singleton de
   * module (`window.b`) qui survivait à tous les rendus. Ici le cycle de vie
   * est accroché au montage de `BrainBoard`, or Vue le détruit et le recrée
   * dans une situation parfaitement ordinaire : le changement de langue —
   * `App.vue` porte `:key="currentLocale…"` sur `<main>` et remonte tout
   * l'arbre.
   *
   * Si `start()` refaisait son amorçage à chaque fois, il rejouerait un
   * `PATCH { start: true }` ou un `getBrainNextItem()` alors que le test n'a
   * pas bougé. Tant qu'on a déjà un item en mémoire, on se contente donc de
   * reconstruire la scène — aucun appel réseau.
   */
  async function start() {
    const tuto = useBrainTutoStore()
    const mode = tuto.isTutorial ? "tuto" : "test"

    // Remontage pur : la file décrit déjà le mode courant, on rejoue la scène.
    if (runner.data.length > 0 && dataMode === mode) {
      await showItem(tuto.isTutorial ? tuto.currentTutoStep : 0)
      return
    }

    // Sinon la file est périmée — sortie de tutoriel, redémarrage — et on
    // repart d'un amorçage complet.
    runner.data.length = 0
    dataMode = mode

    if (tuto.isTutorial) {
      for (const step of TUTO_DATA) {
        addItem(step as unknown as BrainItem)
      }
      await showItem(tuto.currentTutoStep)
    } else {
      // `startTest()` récupère le premier item et nous le transmet par
      // `addItem` : plus d'ordre d'initialisation à respecter, contrairement à
      // l'ancien `data.push` qui exigeait une instance déjà en place.
      await useAnswersStore().startTest()
      await showItem(0)
    }
  }

  /**
   * Arrête le brain. Appelé par `BrainBoard` à son démontage.
   *
   * Ne touche PAS à `runner.data` : le démontage ne signifie pas que le test
   * est fini, seulement que la scène quitte l'écran. Vider la file ici jetait
   * l'item suivant à chaque respiration et à chaque changement de langue.
   * La file n'est purgée qu'aux vraies transitions — sortie du tutoriel et
   * fin du test.
   */
  function stop() {
    runner.reset()
    runner.currentTest.value = -1
  }

  // ─────────────────────────────────────────────────── état dérivé de l'UI

  /**
   * brain.js:1616-1620 — le CTA « valider » remplace « passer » dès que toutes
   * les dropzones sont remplies.
   *
   * brain.js le faisait à la fin de chaque drag, impérativement. Ici l'état
   * est DÉRIVÉ : aucun chemin ne peut oublier de le mettre à jour.
   */
  watch(
    () => runner.allDropzonesFilled.value,
    (filled) => {
      const layout = useBrainLayoutStore()
      if (filled) layout.showConfirmCTA()
      else layout.showSkipCTA()
    }
  )

  /** brain.js:1608-1613 — un déplacement compte quand la dropzone change. */
  watch(
    () => runner.interaction.value?.moveCount.value ?? 0,
    (count, previous) => {
      if (count > previous) {
        useBrainLayoutStore().trackMoves += count - previous
      }
    }
  )

  return {
    runner,
    isRunning,
    isPlayingAnswer,
    start,
    stop,
    addItem,
    showItem,
    sendAnswer,
    rawResetTest,
    playAnswer,
    onSubmitTutoStep,
    nextTutorialStep,
    setDropzonesBlink,
    userIsIdle,
  }
})
